import {
  Avatar,
  Box,
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { useDebounce } from '../../../hooks/ui/useDebounce';
import { apiService } from '../../../services/api.service';
import { getWorkspaceMembers } from '../../../services/api/workspace.api';
import type { EntityPermission } from '../../../hooks/data/useEntityAccess';
import { getAvatarColor } from '../../../utils/avatarColor';
import { permissionLabel } from './PersonRow';

interface AddMemberRowProps {
  entityId: string;
  onAdd: (userId: string, permission: EntityPermission) => Promise<void>;
  excludeUserIds: ReadonlyArray<string>;
  isPending: boolean;
}

interface MemberSearchResult {
  id: string;
  name: string;
  email: string;
  role: string;
}

/**
 * Typeahead row for adding a workspace member to the entity's ACL.
 * Renders only in RESTRICTED mode.
 *
 * Search debounces 300ms before firing — keeps keystroke load on the
 * backend bounded.  Results filter out members already on the ACL
 * (passed via `excludeUserIds`).  The picker carries a default
 * permission selector (View / Edit / Manage) so the user can grant a
 * batch of people at the same level without re-selecting per pick.
 */
export function AddMemberRow({
  entityId: _entityId,
  onAdd,
  excludeUserIds,
  isPending,
}: AddMemberRowProps): React.ReactElement {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ReadonlyArray<MemberSearchResult>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [defaultPermission, setDefaultPermission] = useState<EntityPermission>('VIEW');

  const debouncedQuery = useDebounce(query, 300);

  useEffect(() => {
    let cancelled = false;
    const trimmed = debouncedQuery.trim();
    if (trimmed.length === 0) {
      setResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    void (async () => {
      try {
        const res = await getWorkspaceMembers(apiService, undefined, {
          search: trimmed,
          limit: 8,
        });
        if (cancelled) return;
        if (res.success) {
          const list = (res.data ?? []) as MemberSearchResult[];
          setResults(list.filter((m) => !excludeUserIds.includes(m.id)));
        }
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    })();
    return () => {
      // Discard results from in-flight requests when the query changes
      // again before they resolve — last-write-wins, no out-of-order
      // result flashes.
      cancelled = true;
    };
  }, [debouncedQuery, excludeUserIds]);

  const handlePick = async (member: MemberSearchResult): Promise<void> => {
    await onAdd(member.id, defaultPermission);
    setQuery('');
    setResults([]);
  };

  return (
    <Box>
      <HStack spacing={2}>
        <InputGroup size="sm" flex={1}>
          <InputLeftElement pointerEvents="none">
            <MaterialIcon name="person_add" boxSize="16px" color="text.subtle" />
          </InputLeftElement>
          <Input
            placeholder="Add a workspace member by name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            isDisabled={isPending}
            bg="bg.surface"
          />
        </InputGroup>
        <Menu>
          <MenuButton
            as={Button}
            variant="outline"
            size="sm"
            rightIcon={<MaterialIcon name="arrow_drop_down" boxSize="16px" />}
            fontWeight="medium"
          >
            {permissionLabel(defaultPermission)}
          </MenuButton>
          <MenuList minW="160px">
            {(['VIEW', 'EDIT', 'MANAGE'] as const).map((p) => (
              <MenuItem key={p} onClick={() => setDefaultPermission(p)}>
                <HStack spacing={2}>
                  {p === defaultPermission && <MaterialIcon name="check" boxSize="16px" />}
                  <Text>{permissionLabel(p)}</Text>
                </HStack>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      </HStack>

      {(results.length > 0 || (query.length > 0 && !isSearching)) && (
        <Box
          mt={2}
          maxH="240px"
          overflowY="auto"
          borderRadius="md"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="bg.surface"
        >
          {results.length === 0 && query.length > 0 ? (
            <Box px={3} py={2}>
              <Text fontSize="sm" color="text.subtle">
                No matching workspace members.
              </Text>
            </Box>
          ) : (
            results.map((m) => (
              <Box
                key={m.id}
                as="button"
                type="button"
                w="100%"
                px={3}
                py={2}
                textAlign="left"
                _hover={{ bg: 'bg.hover' }}
                onClick={() => void handlePick(m)}
                borderBottomWidth="1px"
                borderColor="border.subtle"
                _last={{ borderBottomWidth: 0 }}
              >
                <HStack spacing={2}>
                  <Avatar
                    size="xs"
                    name={m.name}
                    bg={getAvatarColor(m.id).bg}
                    color={getAvatarColor(m.id).color}
                  />
                  <VStack align="flex-start" spacing={0} flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="medium" color="text.primary" noOfLines={1}>
                      {m.name}
                    </Text>
                    {/* `text.onMutedBg`, not `text.subtle`: this row's hover was
                        dead (`bg.surface.hover` was never defined), so no text
                        ever sat on a tint here. Now that it paints `bg.hover`,
                        the plain grey is 4.25:1 — the number theme/index.ts
                        names for this exact tint. Same reasoning as the picked
                        row in TransferOwnershipDialog. */}
                    <Text fontSize="xs" color="text.onMutedBg" noOfLines={1}>
                      {m.email}
                    </Text>
                  </VStack>
                  <MaterialIcon name="add" boxSize="16px" color="text.subtle" />
                </HStack>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}
